/**
 * Browser-only WebSocket client service
 * This service is specifically designed to work in browser environments
 * and is safe for Next.js edge runtime
 */

import {
	WebSocketMessage,
	TransferProgress,
	TransferResponse,
} from "../types/websocket.types";

export type ClientWebSocketConnectionState =
	| "disconnected"
	| "connecting"
	| "connected"
	| "reconnecting"
	| "failed";

export interface ClientWebSocketConfig {
	url: string;
	maxReconnectAttempts?: number;
	reconnectBaseDelay?: number;
	maxReconnectDelay?: number;
	heartbeatInterval?: number;
	connectionTimeout?: number;
	messageTimeout?: number;
}

export interface ClientWebSocketEventListeners {
	onStateChange?: (state: ClientWebSocketConnectionState) => void;
	onMessage?: (message: WebSocketMessage) => void;
	onError?: (error: Error) => void;
	onTransferProgress?: (progress: TransferProgress) => void;
	onTransferComplete?: (response: TransferResponse) => void;
}

export class ClientWebSocketManager {
	private static instance: ClientWebSocketManager | null = null;
	private ws: WebSocket | null = null;
	private connectionState: ClientWebSocketConnectionState = "disconnected";
	private reconnectAttempts = 0;
	private reconnectTimer: number | null = null;
	private heartbeatTimer: number | null = null;
	private isIntentionallyDisconnected = false;
	private pendingMessages: WebSocketMessage[] = [];
	private messageQueue: Map<
		string,
		{
			resolve: (value: any) => void;
			reject: (error: Error) => void;
			timeout: number;
		}
	> = new Map();

	private config: Required<ClientWebSocketConfig>;
	private listeners: ClientWebSocketEventListeners = {};

	private constructor(config: ClientWebSocketConfig) {
		// Ensure we're in a browser environment
		if (typeof window === "undefined" || typeof WebSocket === "undefined") {
			throw new Error(
				"ClientWebSocketManager can only be used in browser environment"
			);
		}

		this.config = {
			url: config.url,
			maxReconnectAttempts: config.maxReconnectAttempts ?? 5,
			reconnectBaseDelay: config.reconnectBaseDelay ?? 1000,
			maxReconnectDelay: config.maxReconnectDelay ?? 30000,
			heartbeatInterval: config.heartbeatInterval ?? 30000,
			connectionTimeout: config.connectionTimeout ?? 10000,
			messageTimeout: config.messageTimeout ?? 30000,
		};

		// Start connecting immediately
		this.connect();
	}

	/**
	 * Get the singleton instance of ClientWebSocketManager
	 */
	public static getInstance(
		config?: ClientWebSocketConfig
	): ClientWebSocketManager {
		if (!ClientWebSocketManager.instance) {
			if (!config) {
				throw new Error(
					"ClientWebSocketManager config required for first initialization"
				);
			}
			ClientWebSocketManager.instance = new ClientWebSocketManager(config);
		}
		return ClientWebSocketManager.instance;
	}

	/**
	 * Destroy the singleton instance
	 */
	public static destroyInstance(): void {
		if (ClientWebSocketManager.instance) {
			ClientWebSocketManager.instance.disconnect();
			ClientWebSocketManager.instance = null;
		}
	}

	/**
	 * Set event listeners
	 */
	public setListeners(listeners: ClientWebSocketEventListeners): void {
		this.listeners = { ...this.listeners, ...listeners };
	}

	/**
	 * Get current listeners
	 */
	public getListeners(): ClientWebSocketEventListeners {
		return { ...this.listeners };
	}

	/**
	 * Get current connection state
	 */
	public getConnectionState(): ClientWebSocketConnectionState {
		return this.connectionState;
	}

	/**
	 * Check if connected
	 */
	public isConnected(): boolean {
		return (
			this.connectionState === "connected" &&
			this.ws?.readyState === WebSocket.OPEN
		);
	}

	/**
	 * Connect to WebSocket server
	 */
	public async connect(): Promise<void> {
		if (this.connectionState === "connecting" || this.isConnected()) {
			return Promise.resolve();
		}

		return new Promise((resolve, reject) => {
			this.isIntentionallyDisconnected = false;
			this.setConnectionState("connecting");

			try {
				// Close existing connection if any
				this.closeConnection();

				this.ws = new WebSocket(this.config.url);
				this.ws.binaryType = "arraybuffer";

				const connectionTimeout = window.setTimeout(() => {
					this.ws?.close();
					this.handleConnectionError(new Error("Connection timeout"));
					reject(new Error("WebSocket connection timeout"));
				}, this.config.connectionTimeout);

				this.ws.onopen = () => {
					window.clearTimeout(connectionTimeout);
					this.reconnectAttempts = 0;
					this.setConnectionState("connected");
					this.startHeartbeat();
					this.processPendingMessages();
					resolve();
				};

				this.ws.onclose = (event) => {
					window.clearTimeout(connectionTimeout);
					this.stopHeartbeat();

					if (!this.isIntentionallyDisconnected) {
						this.handleConnectionLoss(event);
					} else {
						this.setConnectionState("disconnected");
					}
				};

				this.ws.onerror = () => {
					window.clearTimeout(connectionTimeout);
					this.handleConnectionError(new Error("WebSocket connection error"));
					reject(new Error("WebSocket connection failed"));
				};

				this.ws.onmessage = (event) => {
					this.handleMessage(event);
				};
			} catch (error) {
				this.handleConnectionError(error as Error);
				reject(error);
			}
		});
	}

	/**
	 * Disconnect from WebSocket server
	 */
	public disconnect(): void {
		this.isIntentionallyDisconnected = true;
		this.clearReconnectTimer();
		this.stopHeartbeat();
		this.closeConnection();
		this.clearMessageQueue();
		this.setConnectionState("disconnected");
	}

	/**
	 * Send a message and wait for response
	 */
	public async sendMessage<T = any>(
		message: WebSocketMessage,
		waitForResponse = false,
		responseType?: string
	): Promise<T | void> {
		// Ensure connection is established before sending
		await this.connect();
		await this.waitForConnection();

		if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
			throw new Error("WebSocket not connected after waiting");
		}

		try {
			const messageStr = JSON.stringify(message);
			this.ws.send(messageStr);

			if (waitForResponse && responseType) {
				return this.waitForMessage<T>(responseType);
			}
		} catch (error) {
			this.handleConnectionError(error as Error);
			throw new Error("Failed to send WebSocket message");
		}
	}

	/**
	 * Wait for a specific message type
	 */
	private waitForMessage<T>(messageType: string): Promise<T> {
		return new Promise((resolve, reject) => {
			const messageId = `${messageType}-${Date.now()}`;

			const timeout = window.setTimeout(() => {
				this.messageQueue.delete(messageId);
				reject(new Error(`Timeout waiting for ${messageType} response`));
			}, this.config.messageTimeout);

			this.messageQueue.set(messageId, {
				resolve,
				reject,
				timeout,
			});
		});
	}

	/**
	 * Wait for connection to be established
	 */
	private async waitForConnection(timeout = 10000): Promise<void> {
		return new Promise((resolve, reject) => {
			if (this.isConnected()) {
				resolve();
				return;
			}

			const timeoutId = window.setTimeout(() => {
				reject(new Error("Timeout waiting for WebSocket connection"));
			}, timeout);

			const checkConnection = () => {
				if (this.isConnected()) {
					window.clearTimeout(timeoutId);
					resolve();
				} else if (this.connectionState === "failed") {
					window.clearTimeout(timeoutId);
					reject(new Error("WebSocket connection failed"));
				} else {
					window.setTimeout(checkConnection, 100);
				}
			};

			checkConnection();
		});
	}

	/**
	 * Handle incoming messages
	 */
	private handleMessage(event: MessageEvent): void {
		try {
			const data = JSON.parse(event.data) as WebSocketMessage;

			// Check for queued message responses
			for (const [messageId, handler] of Array.from(
				this.messageQueue.entries()
			)) {
				if (messageId.startsWith(data.type)) {
					window.clearTimeout(handler.timeout);
					this.messageQueue.delete(messageId);
					handler.resolve(data.payload);
					return;
				}
			}

			// Handle specific message types
			switch (data.type) {
				case "transfer-update":
					this.listeners.onTransferProgress?.(data.payload as TransferProgress);
					break;
				case "transfer-complete":
					this.listeners.onTransferComplete?.(data.payload as TransferResponse);
					break;
				case "pong":
					// Handle pong messages for heartbeat responses
					// This is handled by the general message listener for tests
					break;
				case "error":
					this.listeners.onError?.(
						new Error((data.payload as any).error || "WebSocket error")
					);
					break;
			}

			// General message listener - this is where pong messages will be handled
			this.listeners.onMessage?.(data);
		} catch (error) {
			this.listeners.onError?.(new Error("Failed to parse WebSocket message"));
		}
	}

	/**
	 * Handle connection loss
	 */
	private handleConnectionLoss(event: CloseEvent): void {
		console.warn("WebSocket connection lost:", event.code, event.reason);
		this.setConnectionState("disconnected");
		this.listeners.onError?.(new Error("WebSocket connection lost"));
	}

	/**
	 * Handle connection errors
	 */
	private handleConnectionError(error: Error): void {
		console.error("WebSocket connection error:", error);
		this.setConnectionState("failed");
		this.listeners.onError?.(error);
	}

	/**
	 * Schedule reconnection attempt
	 */
	private scheduleReconnect(): void {
		this.clearReconnectTimer();

		const delay = Math.min(
			this.config.reconnectBaseDelay * Math.pow(2, this.reconnectAttempts),
			this.config.maxReconnectDelay
		);

		this.reconnectTimer = window.setTimeout(() => {
			this.reconnectAttempts++;
			this.connect().catch((error) => {
				console.error("Reconnection failed:", error);
				if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
					this.setConnectionState("failed");
					this.listeners.onError?.(
						new Error("Max reconnection attempts reached")
					);
				} else {
					this.scheduleReconnect();
				}
			});
		}, delay);
	}

	/**
	 * Process pending messages after connection is established
	 */
	private processPendingMessages(): void {
		const messages = [...this.pendingMessages];
		this.pendingMessages = [];

		messages.forEach((message) => {
			this.sendMessage(message).catch((error) => {
				this.listeners.onError?.(error);
			});
		});
	}

	/**
	 * Start heartbeat to keep connection alive
	 */
	private startHeartbeat(): void {
		this.stopHeartbeat();

		this.heartbeatTimer = window.setInterval(() => {
			if (this.ws?.readyState === WebSocket.OPEN) {
				// Send heartbeat message (browser WebSocket doesn't have ping method)
				try {
					this.ws.send(
						JSON.stringify({ type: "ping", payload: { timestamp: Date.now() } })
					);
				} catch (error) {
					// If sending fails, the connection is likely broken
					this.handleConnectionError(new Error("Heartbeat failed"));
				}
			}
		}, this.config.heartbeatInterval);
	}

	/**
	 * Stop heartbeat
	 */
	private stopHeartbeat(): void {
		if (this.heartbeatTimer !== null) {
			window.clearInterval(this.heartbeatTimer);
			this.heartbeatTimer = null;
		}
	}

	/**
	 * Clear reconnection timer
	 */
	private clearReconnectTimer(): void {
		if (this.reconnectTimer !== null) {
			window.clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
	}

	/**
	 * Close WebSocket connection
	 */
	private closeConnection(): void {
		if (this.ws) {
			this.ws.onopen = null;
			this.ws.onclose = null;
			this.ws.onerror = null;
			this.ws.onmessage = null;

			if (
				this.ws.readyState === WebSocket.OPEN ||
				this.ws.readyState === WebSocket.CONNECTING
			) {
				this.ws.close();
			}

			this.ws = null;
		}
	}

	/**
	 * Clear message queue
	 */
	private clearMessageQueue(): void {
		for (const handler of Array.from(this.messageQueue.values())) {
			window.clearTimeout(handler.timeout);
			handler.reject(new Error("Connection closed"));
		}
		this.messageQueue.clear();
	}

	/**
	 * Set connection state and notify listeners
	 */
	private setConnectionState(state: ClientWebSocketConnectionState): void {
		if (this.connectionState !== state) {
			this.connectionState = state;
			this.listeners.onStateChange?.(state);
		}
	}
}
